import React, { useState, useMemo } from 'react'
import { History } from 'history'
import { useDispatch } from 'react-redux'
import ReactMarkdown from 'react-markdown'

import ToggleBox from './toggle-box'
import onboardingSectionData from '../static-copy/onboarding-section-data.json'
import { logActionAsync } from '../store/actions'
import Button from './button'
import { calendarBookingLink } from '../core/variables'
import styles from '../component-styles/welcome-checklist.module.scss'

interface WelcomeChecklistProps {
  fwdRef?: any
  history: History
  fullOnboardingSections: OnboardingSection[]
  setFullOnboardingSections: any
}

const WelcomeChecklist = React.memo(
  ({
    fwdRef = null,
    history,
    fullOnboardingSections,
    setFullOnboardingSections,
  }: WelcomeChecklistProps) => {
    const dispatch = useDispatch()

    const boxCompletedStates = useMemo(() => {
      return fullOnboardingSections.map((section) => {
        if (
          section.sectionCompleted &&
          section.sectionSkippedBefore &&
          !section.sectionSkipped
        ) {
          return false
        }
        if (section.sectionCompleted || section.sectionSkipped) {
          return true
        }
        return false
      })
    }, [fullOnboardingSections])

    const analyticsSection = fullOnboardingSections.find(
      (section) => section.onboardingSectionID === 'connectAnalytics',
    )

    const [boxOpenStates, setBoxOpenStates] = useState(
      new Array(fullOnboardingSections.length).fill(false),
    )

    const refs = {}
    const setRef = (ref, index): void => {
      refs[index] = ref
    }

    const onToggle = (value, index) => {
      const copy = [...boxOpenStates]
      copy[index] = value
      setBoxOpenStates(copy)
    }

    const updateLog = (index, section, state = true) => {
      const copy = JSON.parse(JSON.stringify(fullOnboardingSections))
      if (state && !copy[index].sectionCompleted) {
        copy[index].sectionCompleted = true
        setFullOnboardingSections(copy)
        if (onboardingSectionData[section].actionData) {
          dispatch(logActionAsync(onboardingSectionData[section].actionData))
        }
      }
    }

    const skipStep = (newState, onboardingStep, index) => {
      const copy = JSON.parse(JSON.stringify(fullOnboardingSections))
      copy[index].sectionSkipped = newState
      copy[index].sectionSkippedBefore = true
      setFullOnboardingSections(copy)

      if (copy[index].sectionSkipped) {
        dispatch(
          logActionAsync(onboardingSectionData[onboardingStep].skipAction),
        )
      } else {
        dispatch(
          logActionAsync(onboardingSectionData[onboardingStep].unskipAction),
        )
      }
    }

    const jsxContent = {
      learnUplifter: (
        <>
          <div style={{ margin: '0 0 2em', width: '100%' }}>
            <video width="100%" playsInline controls>
              <source
                src="https://uplifter.ai/wp-content/uploads/2021/07/Uplifter-Explainer-Video-1080p-210324.mp4"
                type="video/mp4"
              />
              <track
                src="captions_en.vtt"
                kind="captions"
                label="english_captions"
              />
            </video>
          </div>
          <div>
            <p>
              Uplifter helps you create and manage your campaign links. No more
              messy spreadsheets!
            </p>
            <ul>
              <li>
                Get everyone using the same structure for links and eliminate
                mistakes altogether
              </li>
              <li>
                Keep all your codes in one place and review their performance
              </li>
              <li>
                Get alerts of problems in your campaigns, and Increase your
                marketing ROI
              </li>
            </ul>
            <p>
              With Uplifter, every ad, every email, every clickthrough is
              tracked right, every single time.
            </p>
            {
              // TODO: add this section back in once onboarding PDF is ready
            }
            {/* <p>
                For a more detailed view, take a look at our quick start guide{' '}
                <a href="/welcome#">here</a>.
              </p> */}
          </div>
        </>
      ),
      createCampaignLink: (
        <>
          <p>Let’s create a simple campaign link.</p>
          <p>
            To start, you can use Uplifter’s generic template – you can
            customise it later.
          </p>
          <ol>
            <li>
              Go to the <span className={styles.boxed}>Track&gt;Create</span>{' '}
              page
            </li>
            <li>Add your Landing page URL</li>
            <li>Select your variables from the dropdown fields</li>
            <li>Click ‘Create campaign link’</li>
          </ol>
          <p>
            Your new link will appear on the right side of the page. You can
            download it or share it with whoever needs it.
          </p>
          <p>
            You can view all your links on the{' '}
            <span className={styles.boxed}>Track&gt;View</span> page. This also
            shows how well your previous campaigns performed.
          </p>
        </>
      ),
      editTaxonomy: (
        <>
          <p>
            If you need custom rules and parameters, Admin users can set them up
            on the <span className={styles.boxed}>Track&gt;Edit</span> page.
          </p>
          <p>From here you can:</p>
          <ul>
            <li>
              Add or remove required, optional or meta campaign link parameters
            </li>
            <li>
              Set a list of values for users to choose for each parameter when
              creating new links
            </li>
            <li>Add free text options</li>
            <li>
              Define how parameters should be separated in the URL (e.g. with
              ‘&amp;’ or with ‘|’) and whether spaces or uppercase should be
              allowed
            </li>
            <li>Approve new parameter requests from Regular users</li>
          </ul>
        </>
      ),
      connectAnalytics: (
        <>
          <p>
            There are no Uplifter tags on your website - Uplifter uses data from
            your web analytics platform to measure campaign performance. Just
            connect to Google or Adobe Analytics, and the data will start
            rolling in!
          </p>
          <p>
            If you have any live campaigns, you’ll see their performance on the{' '}
            <span className={styles.boxed}>Track&gt;View</span> page.
          </p>
          {!analyticsSection?.sectionCompleted && (
            <p>
              Click the 'Connect analytics' button below and follow the
              instructions.
            </p>
          )}
          {analyticsSection?.sectionCompleted && (
            <p style={{ fontWeight: 'bold' }}>
              Your website is already connected to your analytics tool.
            </p>
          )}
        </>
      ),
    }

    if (Array.isArray(boxCompletedStates)) {
      return (
        <div className={styles.checklistContainer} ref={fwdRef}>
          {fullOnboardingSections.map(
            (
              {
                onboardingSectionID,
                sectionCompleted,
                sectionSkipped,
                sectionSkippedBefore,
              },
              index,
            ) => {
              if (
                !onboardingSectionID ||
                !onboardingSectionData[onboardingSectionID]
              ) {
                return <></>
              }

              const sectionFull = onboardingSectionData[onboardingSectionID]
              return (
                <ToggleBox
                  className={styles.toggleBox}
                  key={onboardingSectionID}
                  fwdRef={(self) => {
                    setRef(self, index)
                  }}
                  heading={sectionFull.title}
                  checked={boxCompletedStates[index]}
                  checkedLeft
                  open={boxOpenStates[index]}
                  onToggle={(state: boolean) => {
                    onToggle(state, sectionFull[index])
                    if (onboardingSectionID === 'learnUplifter') {
                      updateLog(index, onboardingSectionID, state)
                    }
                  }}
                  strikethrough
                >
                  <div className={styles.toggleInner}>
                    {!!sectionFull.content && (
                      <ReactMarkdown>{sectionFull.content}</ReactMarkdown>
                    )}
                    {!sectionFull.content && jsxContent[onboardingSectionID]}
                    <div className={styles.buttonsContainer}>
                      {(!!sectionFull.link || !!sectionFull.linkCopy) && (
                        <Button
                          onClick={(): void => {
                            if (sectionFull.link === 'bookSession') {
                              window.open(calendarBookingLink, '_blank')
                              updateLog(index, onboardingSectionID)
                            } else if (
                              onboardingSectionID === 'connectAnalytics' &&
                              sectionCompleted
                            ) {
                              history.push('/settings?show=connect')
                            } else {
                              history.push(sectionFull.link)
                            }
                          }}
                        >
                          {onboardingSectionID === 'connectAnalytics' &&
                            sectionCompleted && <>View connected websites</>}
                          {!(
                            onboardingSectionID === 'connectAnalytics' &&
                            sectionCompleted
                          ) && sectionFull.linkCopy}
                        </Button>
                      )}
                      {!!sectionFull.skipAction && (
                        // {!!sectionFull.skipAction && !sectionCompleted && (
                        <Button
                          type="flatPink"
                          className={
                            onboardingSectionID === 'learnUplifter'
                              ? styles.markCompleteRight
                              : ''
                          }
                          onClick={(): void => {
                            const status =
                              !sectionSkippedBefore && sectionCompleted
                                ? false
                                : !sectionSkipped
                            skipStep(status, onboardingSectionID, index)
                          }}
                        >
                          <span>
                            Mark as {boxCompletedStates[index] ? 'in' : ''}
                            complete
                          </span>
                        </Button>
                      )}
                    </div>
                  </div>
                </ToggleBox>
              )
            },
          )}
        </div>
      )
    }
    return <></>
  },
)

export default WelcomeChecklist
